var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Banner, FieldLabel, FlexBox, FormField, FormGrid, } from '@dixa/lib-shared-habla';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { createOAuthProviderMutation, updateOAuthProviderMutation, } from 'apis/partnerIntegrations';
import { analytics } from 'app/utils/segment';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { FormActions } from './components/actions/actions';
import { SchemaField } from './components/schemaField/schemaField';
import styles from './oAuthForm.module.scss';
import { mapSchemaToSettings } from './utils/mapProviderToSchema/mapProviderToSchema';
/** Represents the OAuth form. */
export const OAuthForm = ({ disabled, selectedStrategy, oAuthSchema, oAuthProvider, onChangeParentOAuthId, setOAuthProvider, handleParentSubmit, }) => {
    const notify = useNotifications();
    const { control, handleSubmit, reset } = useFormContext();
    const hasSchemaFields = oAuthSchema.length > 0;
    const oAuthConnectionId = oAuthProvider === null || oAuthProvider === void 0 ? void 0 : oAuthProvider.id;
    const hasProvider = !!oAuthProvider;
    const hasNewStrategy = selectedStrategy.name !== (oAuthProvider === null || oAuthProvider === void 0 ? void 0 : oAuthProvider.strategy);
    const hasFormValues = hasNewStrategy ? false : hasProvider;
    const [createOAuthProvider, { loading: isCreateOAuthProviderLoading }] = useMutation(createOAuthProviderMutation, {
        onError: (err) => {
            notify({
                title: 'Error while creating the OAuth provider.',
                text: err,
            }, NotificationType.Error);
        },
        onCompleted: (result) => {
            const provider = result.Management.createOAuthProvider;
            handleOnComplete(provider);
            // Update the OAuth provider hook state with the new provider
            setOAuthProvider(provider);
            // Update the custom card form state with the new provider id
            onChangeParentOAuthId(provider.id);
            // Also save the custom card with the new provider id
            handleParentSubmit();
            analytics.settingsIntegrationsCustomCardOauthProviderCreated({
                oAuthProviderId: provider.id,
                oAuthStrategy: selectedStrategy.name,
            });
        },
    });
    const [updateOAuthProvider, { loading: isUpdateOAuthProviderLoading }] = useMutation(updateOAuthProviderMutation, {
        onError: (err) => {
            notify({
                title: 'Error while updating the OAuth provider.',
                text: err,
            }, NotificationType.Error);
        },
        onCompleted: (result) => {
            handleOnComplete(result.Management.updateOAuthProvider);
            const provider = result.Management.updateOAuthProvider;
            analytics.settingsIntegrationsCustomCardOauthProviderUpdated({
                oAuthProviderId: provider.id,
                oAuthStrategy: selectedStrategy.name,
            });
        },
    });
    const handleOnComplete = (provider) => {
        const { name, id, settings } = provider;
        reset(Object.assign(Object.assign({}, mapSchemaToSettings({
            schema: oAuthSchema,
            settings,
        })), { strategy: selectedStrategy.name, name,
            id }));
        notify({
            title: 'Success',
            text: `OAuth provider ${name} successfully ${hasProvider ? 'updated' : 'created'}.`,
            icon: 'i-done',
            time: 5000,
        }, NotificationType.Success);
    };
    const onSubmit = (formValues) => __awaiter(void 0, void 0, void 0, function* () {
        const { name, id } = formValues, settings = __rest(formValues, ["name", "id"]);
        // We only want to updated, when there is already a provider and the strategy is not changed
        if (hasProvider && !hasNewStrategy) {
            updateOAuthProvider({
                variables: {
                    id: id,
                    name,
                    settings: Object.keys(settings)
                        // Filtering out unchanged secrets
                        .filter((key) => { var _a; return (_a = settings[key]) === null || _a === void 0 ? void 0 : _a.isEditing; })
                        .map((key) => ({
                        key,
                        value: settings[key].value,
                        secret: settings[key].secret,
                    })),
                },
            });
        }
        else {
            createOAuthProvider({
                variables: {
                    name,
                    strategy: selectedStrategy.name,
                    settings: Object.keys(settings).map((key) => ({
                        key,
                        value: settings[key].value,
                        secret: settings[key].secret,
                    })),
                },
            });
        }
    });
    // Effect to populate the form state
    useEffect(() => {
        if (hasProvider) {
            const { name, id, settings } = oAuthProvider;
            // Reset the form if the strategy got changed
            if (hasNewStrategy) {
                reset(Object.assign(Object.assign({}, mapSchemaToSettings({
                    schema: oAuthSchema,
                })), { name: '', id: undefined }));
            }
            else {
                //...otherwise populate the form with the provider data
                reset(Object.assign(Object.assign({}, mapSchemaToSettings({
                    schema: oAuthSchema,
                    settings,
                })), { name,
                    id }));
            }
        }
        else if (hasSchemaFields) {
            const formSchema = mapSchemaToSettings({ schema: oAuthSchema });
            // Sets the form state based on the schema with empty values
            reset(formSchema);
        }
    }, [
        hasProvider,
        hasNewStrategy,
        hasSchemaFields,
        oAuthProvider,
        oAuthSchema,
        reset,
    ]);
    return (_jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "200", margin: "0 0 300 0" }, { children: [_jsx(FieldLabel, { children: `${selectedStrategy.label} configuration` }), _jsxs(FormGrid, Object.assign({ className: styles.formWrapper }, { children: [!hasSchemaFields && (_jsx(FormField, { children: "This strategy does not have any input fields defined." })), hasSchemaFields && (_jsxs(_Fragment, { children: [_jsx(FormField, { children: _jsx(HookFormText, { disabled: disabled, label: "Name", name: 'name', control: control, componentProps: {
                                        placeholder: 'Insert name',
                                    }, rules: {
                                        required: {
                                            value: true,
                                            message: 'Please add a name for your OAuth provider',
                                        },
                                    } }) }), oAuthSchema.map((schema) => (_jsx(SchemaField, { schema: schema, disabled: disabled, hasFormValues: hasFormValues }, schema.key))), hasProvider && !oAuthConnectionId && (_jsx(FormField, { children: _jsx(Banner, { variant: "warning", headline: "Unsaved custom card", description: _jsx(_Fragment, { children: "You have to save the custom card in order to pair the card with the OAuth provider." }) }) })), _jsx(FormField, { children: _jsx(FormActions, { disabled: disabled, hasFormValues: hasFormValues, isSaving: isCreateOAuthProviderLoading || isUpdateOAuthProviderLoading, onSubmit: handleSubmit(onSubmit), oAuthProviderId: oAuthConnectionId, oAuthStrategy: selectedStrategy.name }) })] }))] }))] })));
};
